/**
 * Generic admin manager voor één builder-variant.
 * Gebruikt in de Builder Hub als tab-content.
 *
 * Props:
 *   - api: workshopBuilderAPI-stijl object met listAdmin / create / update / remove
 *   - publicPath: bv. "/scrum-builder" — gebruikt om publieke link + embed te genereren
 *   - kindLabel: bv. "Scrum"
 *   - accentClass: bv. "bg-indigo-50 border-indigo-200" — voor de info-card
 *   - newButtonClass: bv. "bg-indigo-600 hover:bg-indigo-700"
 *   - testIdPrefix: voor data-testid
 */
import { useState, useEffect, useCallback } from 'react';
import {
  Plus, Edit2, Trash2, Eye, EyeOff, X, Copy, ExternalLink,
  Sparkles, Heart, Apple, Activity, Zap, Package, Users, Boxes,
  ChevronDown, ChevronUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

const ICON_MAP = { Sparkles, Heart, Apple, Activity, Zap, Package, Users, Boxes };
const ICON_OPTIONS = ['Sparkles', 'Heart', 'Apple', 'Activity', 'Zap', 'Package', 'Users', 'Boxes'];
const BLOCK_TYPES = [
  { value: 'single_choice', label: 'Eén keuze (radio)' },
  { value: 'multi_choice', label: 'Meerdere keuzes (checkbox)' },
  { value: 'text', label: 'Korte tekst' },
  { value: 'textarea', label: 'Lange tekst' },
  { value: 'number', label: 'Getal' },
];

const emptyTemplate = (defaultColor) => ({
  name: '',
  description: '',
  intro_text: '',
  icon: 'Sparkles',
  color: defaultColor || '#10B981',
  active: true,
  sort_order: 99,
  blocks: [],
});

export default function BuilderTemplatesManager({
  api,
  publicPath,
  kindLabel = 'Builder',
  defaultColor = '#10B981',
  accentClass = 'border-emerald-200 bg-emerald-50/40',
  iconBgClass = 'bg-emerald-600',
  newButtonClass = 'bg-emerald-600 hover:bg-emerald-700',
  testIdPrefix = 'builder',
}) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.listAdmin();
      setTemplates(res.data.templates || []);
    } catch {
      toast.error('Kon templates niet laden');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openNew = () => { setEditing(emptyTemplate(defaultColor)); setShowEditor(true); };
  const openEdit = (tpl) => { setEditing(JSON.parse(JSON.stringify(tpl))); setShowEditor(true); };

  const handleSave = async () => {
    if (!editing.name.trim()) { toast.error('Naam is verplicht'); return; }
    try {
      if (editing.id) {
        await api.update(editing.id, editing);
        toast.success('Template bijgewerkt');
      } else {
        await api.create(editing);
        toast.success('Template aangemaakt');
      }
      setShowEditor(false);
      setEditing(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Opslaan mislukt');
    }
  };

  const handleDelete = async () => {
    try {
      await api.remove(confirmDelete.id);
      toast.success('Verwijderd');
      setConfirmDelete(null);
      fetchAll();
    } catch {
      toast.error('Verwijderen mislukt');
    }
  };

  const toggleActive = async (tpl) => {
    try {
      await api.update(tpl.id, { active: !tpl.active });
      fetchAll();
    } catch {
      toast.error('Update mislukt');
    }
  };

  const publicUrl = `${window.location.origin}${publicPath}`;
  const embedSnippet = `<iframe src="${publicUrl}" width="100%" height="900" frameborder="0" style="border:0;border-radius:12px;"></iframe>`;

  const copy = (txt, msg) => {
    navigator.clipboard.writeText(txt);
    toast.success(msg || 'Gekopieerd');
  };

  if (loading) {
    return <div className="p-8 text-center text-muted-foreground">Laden...</div>;
  }

  return (
    <div className="space-y-5" data-testid={`${testIdPrefix}-manager`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold tracking-tight">{kindLabel} templates</h2>
          <p className="text-muted-foreground text-sm">Beheer templates en blokken voor de publieke {kindLabel.toLowerCase()} bouwer.</p>
        </div>
        <Button onClick={openNew} className={`gap-2 ${newButtonClass}`} data-testid={`${testIdPrefix}-new-btn`}>
          <Plus className="w-4 h-4" /> Nieuw Template
        </Button>
      </div>

      {/* Embed info card */}
      <Card className={accentClass}>
        <CardContent className="p-5 space-y-3">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-lg ${iconBgClass} flex items-center justify-center flex-shrink-0`}>
              <ExternalLink className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-sm">Publieke link & embed</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Deel deze link of embed deze bouwer op je website.</p>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Input readOnly value={publicUrl} className="font-mono text-xs h-9" data-testid={`${testIdPrefix}-public-url`} />
              <Button size="sm" variant="outline" onClick={() => copy(publicUrl, 'Link gekopieerd')} className="gap-1.5 flex-shrink-0">
                <Copy className="w-3.5 h-3.5" /> Kopieer
              </Button>
              <Button size="sm" variant="outline" onClick={() => window.open(publicUrl, '_blank')} className="gap-1.5 flex-shrink-0">
                <ExternalLink className="w-3.5 h-3.5" /> Open
              </Button>
            </div>
            <div className="flex items-start gap-2">
              <Textarea readOnly value={embedSnippet} className="font-mono text-xs min-h-[60px]" data-testid={`${testIdPrefix}-embed-snippet`} />
              <Button size="sm" variant="outline" onClick={() => copy(embedSnippet, 'Embed code gekopieerd')} className="gap-1.5 flex-shrink-0">
                <Copy className="w-3.5 h-3.5" /> Kopieer
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Templates list */}
      {templates.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-muted-foreground">Nog geen templates. Klik op "Nieuw Template" om te beginnen.</CardContent></Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {templates.map(tpl => {
            const Icon = ICON_MAP[tpl.icon] || Sparkles;
            return (
              <Card key={tpl.id} className="border-border/60" data-testid={`tpl-card-${tpl.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: `${tpl.color || defaultColor}15` }}>
                      <Icon className="w-6 h-6" style={{ color: tpl.color || defaultColor }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-semibold truncate">{tpl.name}</h3>
                        <Badge variant={tpl.active ? 'default' : 'secondary'} className={tpl.active ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : ''}>
                          {tpl.active ? 'Actief' : 'Inactief'}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{tpl.description}</p>
                      <div className="text-xs text-muted-foreground mt-2">{(tpl.blocks || []).length} bouwblokken</div>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4 pt-3 border-t border-gray-100">
                    <Button size="sm" variant="outline" onClick={() => openEdit(tpl)} className="gap-1.5 flex-1" data-testid={`edit-${tpl.id}`}>
                      <Edit2 className="w-3.5 h-3.5" /> Bewerken
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => toggleActive(tpl)} className="gap-1.5 flex-shrink-0">
                      {tpl.active ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setConfirmDelete(tpl)} className="text-red-600 hover:text-red-700 flex-shrink-0">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={(o) => { if (!o) { setShowEditor(false); setEditing(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Template bewerken' : `Nieuw ${kindLabel.toLowerCase()} template`}</DialogTitle>
          </DialogHeader>
          {editing && <TemplateEditor editing={editing} setEditing={setEditing} />}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditor(false); setEditing(null); }}>Annuleren</Button>
            <Button onClick={handleSave} className={newButtonClass} data-testid={`${testIdPrefix}-save`}>Opslaan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Template verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je "{confirmDelete?.name}" wilt verwijderen? Dit kan niet ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Verwijderen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function TemplateEditor({ editing, setEditing }) {
  const update = (patch) => setEditing(p => ({ ...p, ...patch }));

  const addBlock = () => {
    update({
      blocks: [...(editing.blocks || []), {
        key: `block_${Date.now()}`,
        label: 'Nieuw blok',
        description: '',
        type: 'single_choice',
        options: [],
        required: false,
        allow_custom: false,
      }],
    });
  };
  const updateBlock = (idx, patch) => {
    const blocks = [...editing.blocks];
    blocks[idx] = { ...blocks[idx], ...patch };
    update({ blocks });
  };
  const removeBlock = (idx) => update({ blocks: editing.blocks.filter((_, i) => i !== idx) });
  const moveBlock = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= editing.blocks.length) return;
    const blocks = [...editing.blocks];
    [blocks[idx], blocks[target]] = [blocks[target], blocks[idx]];
    update({ blocks });
  };

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <Label>Naam *</Label>
          <Input value={editing.name} onChange={(e) => update({ name: e.target.value })} placeholder="bijv. Vitaliteit & Energie" data-testid="tpl-name" />
        </div>
        <div className="sm:col-span-2">
          <Label>Korte beschrijving</Label>
          <Textarea value={editing.description || ''} onChange={(e) => update({ description: e.target.value })} rows={2} />
        </div>
        <div className="sm:col-span-2">
          <Label>Intro tekst (boven de blokken)</Label>
          <Textarea value={editing.intro_text || ''} onChange={(e) => update({ intro_text: e.target.value })} rows={2} />
        </div>
        <div>
          <Label>Icoon</Label>
          <select value={editing.icon || 'Sparkles'} onChange={(e) => update({ icon: e.target.value })}
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm">
            {ICON_OPTIONS.map(i => <option key={i} value={i}>{i}</option>)}
          </select>
        </div>
        <div>
          <Label>Kleur</Label>
          <div className="flex gap-2 items-center">
            <input type="color" value={editing.color || '#10B981'} onChange={(e) => update({ color: e.target.value })} className="w-12 h-9 rounded border border-input" />
            <Input value={editing.color || ''} onChange={(e) => update({ color: e.target.value })} className="font-mono text-xs" />
          </div>
        </div>
        <div>
          <Label>Volgorde</Label>
          <Input type="number" value={editing.sort_order ?? 99} onChange={(e) => update({ sort_order: parseInt(e.target.value) || 0 })} />
        </div>
        <div className="flex items-end gap-2">
          <div className="flex items-center gap-2 pb-2">
            <Switch checked={!!editing.active} onCheckedChange={(v) => update({ active: v })} id="active-switch" />
            <Label htmlFor="active-switch" className="cursor-pointer">Actief (zichtbaar voor publiek)</Label>
          </div>
        </div>
      </div>

      <div className="pt-2 border-t border-gray-100">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-sm">Bouwblokken ({editing.blocks?.length || 0})</h3>
          <Button size="sm" variant="outline" onClick={addBlock} className="gap-1.5" data-testid="add-block-btn">
            <Plus className="w-3.5 h-3.5" /> Blok toevoegen
          </Button>
        </div>
        <div className="space-y-3">
          {(editing.blocks || []).map((block, idx) => (
            <BlockEditor
              key={idx}
              block={block}
              idx={idx}
              total={editing.blocks.length}
              onChange={(patch) => updateBlock(idx, patch)}
              onRemove={() => removeBlock(idx)}
              onMove={(dir) => moveBlock(idx, dir)}
            />
          ))}
          {(editing.blocks || []).length === 0 && (
            <div className="text-center text-xs text-muted-foreground py-6 border-2 border-dashed rounded-lg">
              Nog geen blokken. Klik "Blok toevoegen" om te beginnen.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function BlockEditor({ block, idx, total, onChange, onRemove, onMove }) {
  const [expanded, setExpanded] = useState(false);
  const hasOptions = block.type === 'single_choice' || block.type === 'multi_choice';

  const addOption = () => onChange({ options: [...(block.options || []), { label: 'Nieuwe optie', value: '', description: '' }] });
  const updateOption = (oIdx, patch) => {
    const options = [...(block.options || [])];
    options[oIdx] = { ...options[oIdx], ...patch };
    onChange({ options });
  };
  const removeOption = (oIdx) => onChange({ options: (block.options || []).filter((_, i) => i !== oIdx) });

  return (
    <div className="border border-border rounded-lg overflow-hidden bg-white">
      <div className="flex items-center gap-2 p-3 bg-gray-50">
        <div className="flex flex-col gap-0.5">
          <button onClick={() => onMove(-1)} disabled={idx === 0} className="text-gray-400 hover:text-gray-700 disabled:opacity-30">
            <ChevronUp className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => onMove(1)} disabled={idx === total - 1} className="text-gray-400 hover:text-gray-700 disabled:opacity-30">
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">{block.label || 'Naamloos blok'}</div>
          <div className="text-xs text-muted-foreground">{BLOCK_TYPES.find(t => t.value === block.type)?.label} {block.required && '• verplicht'}</div>
        </div>
        <Button size="sm" variant="ghost" onClick={() => setExpanded(e => !e)}>
          {expanded ? 'Inklappen' : 'Bewerken'}
        </Button>
        <Button size="sm" variant="ghost" onClick={onRemove} className="text-red-600 hover:text-red-700">
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
      {expanded && (
        <div className="p-3 space-y-3 border-t border-border">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Label (zichtbaar)</Label>
              <Input value={block.label || ''} onChange={(e) => onChange({ label: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Sleutel (key, intern)</Label>
              <Input value={block.key || ''} onChange={(e) => onChange({ key: e.target.value.replace(/\s+/g, '_').toLowerCase() })} className="font-mono text-xs" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Beschrijving / hulptekst</Label>
              <Input value={block.description || ''} onChange={(e) => onChange({ description: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Type</Label>
              <select value={block.type} onChange={(e) => onChange({ type: e.target.value })}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm">
                {BLOCK_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Placeholder (voor tekst/getal)</Label>
              <Input value={block.placeholder || ''} onChange={(e) => onChange({ placeholder: e.target.value })} />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={!!block.required} onCheckedChange={(v) => onChange({ required: v })} id={`req-${idx}`} />
              <Label htmlFor={`req-${idx}`} className="cursor-pointer text-xs">Verplicht</Label>
            </div>
            {hasOptions && (
              <div className="flex items-center gap-2">
                <Switch checked={!!block.allow_custom} onCheckedChange={(v) => onChange({ allow_custom: v })} id={`cust-${idx}`} />
                <Label htmlFor={`cust-${idx}`} className="cursor-pointer text-xs">Eigen input toestaan ("Anders")</Label>
              </div>
            )}
          </div>

          {hasOptions && (
            <div className="pt-2 border-t border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-semibold">Opties</Label>
                <Button size="sm" variant="outline" onClick={addOption} className="gap-1 h-7 text-xs">
                  <Plus className="w-3 h-3" /> Optie
                </Button>
              </div>
              <div className="space-y-2">
                {(block.options || []).map((opt, oIdx) => (
                  <div key={oIdx} className="flex items-start gap-2 p-2 bg-gray-50 rounded">
                    <div className="flex-1 grid sm:grid-cols-3 gap-2">
                      <Input placeholder="Label" value={opt.label || ''} onChange={(e) => updateOption(oIdx, { label: e.target.value })} className="text-xs h-8" />
                      <Input placeholder="Waarde (optioneel)" value={opt.value || ''} onChange={(e) => updateOption(oIdx, { value: e.target.value })} className="text-xs h-8 font-mono" />
                      <Input placeholder="Beschrijving (optioneel)" value={opt.description || ''} onChange={(e) => updateOption(oIdx, { description: e.target.value })} className="text-xs h-8" />
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => removeOption(oIdx)} className="text-red-600 hover:text-red-700 h-8 w-8 p-0">
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
                {(block.options || []).length === 0 && (
                  <div className="text-center text-xs text-muted-foreground py-3 border border-dashed rounded">Geen opties — voeg er minimaal één toe.</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
